/**
 * Von den Messwerten zur Einstufung: Befund → Probe → Rasterfeld →
 * Arbeitsbereich.
 *
 * Kennt weder DOM noch Supabase noch React; alle Funktionen sind rein.
 *
 * ═══ DIE EINE REGEL, DIE HIER NIEMAND BRECHEN DARF ════════════════════════
 * Es wird NICHT gemittelt. Nicht ueber die Proben eines Feldes, nicht ueber
 * die Felder eines Bereichs, nicht ueber Schichten. Massgeblich ist immer das
 * MAXIMUM.
 *
 * Die Begruendung steht in der TRGS 524 Nr. 4.1 Abs. 15: bei nicht
 * ausreichenden Kenntnissen ueber die Gefahren sind maximale Sicherheits-
 * massnahmen anzusetzen. Ein Mittelwert verduennt einen Hotspot rechnerisch
 * weg — und genau davor warnt die BG BAU bei der Uebernahme abfallrechtlicher
 * Beurteilungen: Mischproben „missbrauchen den Verduennungseffekt".
 *
 * Wer hier je eine Summe durch eine Anzahl teilt, erzeugt eine Zahl, die
 * niemandem auffaellt und die auf dem Blatt aussieht wie ein Messergebnis.
 * Der Test `einstufung.test.ts` prueft das namentlich.
 *
 * ═══ UND DIE ZWEITE REGEL ═════════════════════════════════════════════════
 * Was nicht abgeleitet werden kann, wird NICHT geraten. Die EBV-Einstufung
 * haengt an Bodenart, Eluat und Einbauweise; dieses Modul kennt nur wenige
 * belastbare Schwellen. Wo keine hinterlegt ist, liefert die Funktion den
 * Status `aus_gutachten_uebernehmen` — und nicht etwa BM-0. Ein geratener
 * Vorschlag saehe aus wie ein Ergebnis.
 */

import type { Befund, Probe } from './types';
import { EBV_KLASSEN, bodenBewertung, ebvKlasse, ebvRang } from './kataloge/ebvKlassen';

// ═══════════════════════════════════════════════════════════════════════════
// EINSTUFUNGSRELEVANZ DER EINZELNEN BEFUNDE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Markiert je Befund, ob er in die Einstufung eingeht.
 *
 * Regel aus der EBV, die im A+S-Plan als Kopfzeile der Auswertungstabelle
 * steht: Eluat-Auffaelligkeiten, die nicht im Feststoff nachgewiesen wurden,
 * sind nicht einstufungsrelevant.
 *
 * Der Befund wird deshalb NICHT entfernt, sondern markiert. Ein weggelassener
 * Messwert ist eine stillschweigende Auswahl; ein als „nicht
 * einstufungsrelevant" ausgewiesener Messwert ist eine nachvollziehbare
 * Entscheidung. Im gedruckten Plan erscheinen beide.
 *
 * Gibt eine NEUE Liste zurueck — die Eingabe bleibt unberuehrt, damit ein
 * versehentlicher Aufruf keine gespeicherten Daten veraendert.
 */
export const markiereEinstufungsrelevanz = (befunde: Befund[]): Befund[] => {
    const mitFeststoff = new Set(
        befunde.filter(b => b.medium === 'feststoff' && hatWert(b)).map(b => b.stoff_key),
    );
    return befunde.map(b => ({
        ...b,
        einstufungsrelevant: b.medium === 'feststoff'
            ? hatWert(b)
            : hatWert(b) && mitFeststoff.has(b.stoff_key),
    }));
};

const hatWert = (b: Befund): boolean => b.wert !== null && Number.isFinite(b.wert);

// ═══════════════════════════════════════════════════════════════════════════
// BEFUNDE → EINSTUFUNG EINER PROBE
// ═══════════════════════════════════════════════════════════════════════════

/** Warum eine Einstufung so lautet, wie sie lautet. */
export type AbleitungsStatus =
    /** Aus einer hinterlegten Schwelle abgeleitet. */
    | 'abgeleitet'
    /** Es gibt Befunde, aber fuer keinen ist eine Schwelle hinterlegt. */
    | 'aus_gutachten_uebernehmen'
    /** Keine verwertbaren Befunde vorhanden. */
    | 'keine_befunde';

export interface ProbenEinstufung {
    /** Klassenschluessel oder `null`, wenn nicht ableitbar. `null` ist NICHT
     *  „unbelastet" — dafuer gibt es 'BM-0'. */
    klasse: string | null;
    /** Welcher Stoff die Klasse verursacht hat. Im Musterplan traegt Feld 6
     *  seine Klasse wegen BLEI, obwohl PAK dort niedrig ist. */
    massgeblich_stoff: string;
    status: AbleitungsStatus;
    /** Klartext fuer die Oberflaeche — sagt, was der Bearbeiter tun soll. */
    hinweis: string;
}

/**
 * Leitet die Einstufung EINER Probe aus ihren Befunden ab.
 *
 * Nimmt das Maximum ueber alle einstufungsrelevanten Befunde, fuer die eine
 * Schwelle hinterlegt ist. Ist fuer keinen eine hinterlegt, wird nichts
 * vorgeschlagen.
 */
export const einstufungAusBefunden = (befunde: Befund[]): ProbenEinstufung => {
    const markiert = markiereEinstufungsrelevanz(befunde);
    const relevant = markiert.filter(b => b.einstufungsrelevant);

    if (relevant.length === 0) {
        return {
            klasse: null,
            massgeblich_stoff: '',
            status: 'keine_befunde',
            hinweis: 'Keine einstufungsrelevanten Feststoffbefunde erfasst.',
        };
    }

    let besterRang = -1;
    let besterStoff = '';
    let besteKlasse: string | null = null;
    let schwelleGefunden = false;

    for (const b of relevant) {
        const bewertung = bodenBewertung(b.stoff_key);
        const schwelle = bewertung?.schwelle_bm_f3 ?? null;
        if (schwelle === null || b.wert === null) continue;

        schwelleGefunden = true;
        // Ueber der Schwelle: BM-F3. Darunter wird NICHTS behauptet — dass ein
        // Wert unter der BM-F3-Schwelle liegt, macht ihn nicht zu BM-0; dafuer
        // braucht es die uebrigen EBV-Vergleichswerte, die dieser Katalog
        // bewusst nicht fuehrt.
        if (b.wert >= schwelle) {
            const rang = ebvRang('BM-F3');
            if (rang > besterRang) {
                besterRang = rang;
                besterStoff = b.stoff_key;
                besteKlasse = 'BM-F3';
            }
        }
    }

    if (besteKlasse) {
        return {
            klasse: besteKlasse,
            massgeblich_stoff: besterStoff,
            status: 'abgeleitet',
            hinweis: `Über der hinterlegten Schwelle für ${besterStoff}.`,
        };
    }

    return {
        klasse: null,
        massgeblich_stoff: '',
        status: 'aus_gutachten_uebernehmen',
        hinweis: schwelleGefunden
            ? 'Kein Befund über der hinterlegten Schwelle, die Einstufung ist damit noch '
              + 'nicht bestimmt. Aus dem Gutachten übernehmen.'
            : 'Für die erfassten Stoffe ist keine Schwelle hinterlegt. Einstufung aus dem '
              + 'Gutachten übernehmen.',
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// DAS MAXIMUMPRINZIP
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die hoechste Klasse einer Liste.
 *
 * Unbekannte und leere Schluessel werden uebergangen (`ebvRang` liefert -1),
 * nicht als BM-0 gewertet. Gibt `null` zurueck, wenn nichts Verwertbares
 * dabei ist.
 *
 * Das ist die Funktion, um die es geht. Sie hat absichtlich keine Variante
 * mit Gewichtung, keinen Parameter „Modus" und keinen Mittelwert-Zwilling —
 * es soll gar nicht erst die Moeglichkeit geben, sie falsch aufzurufen.
 */
export const hoechsteEinstufung = (klassen: (string | null | undefined)[]): string | null => {
    let besterRang = -1;
    let beste: string | null = null;
    for (const k of klassen) {
        const rang = ebvRang(k);
        if (rang > besterRang) {
            besterRang = rang;
            beste = k ?? null;
        }
    }
    return besterRang >= 0 ? beste : null;
};

// ═══════════════════════════════════════════════════════════════════════════
// PROBEN → RASTERFELD
// ═══════════════════════════════════════════════════════════════════════════

export interface FeldEinstufung {
    /** Maximum ueber alle Proben des Feldes. */
    klasse: string | null;
    /** Die Probe, die das Maximum gestellt hat. */
    massgebliche_probe: string;
    /**
     * Tiefe (m unter GOK), bis zu der belastetes Material nachgewiesen ist.
     *
     * Das ist die Angabe, aus der im Musterplan die hoechste Schutzklasse
     * folgt: Feld 51 ist bis 2,0 m belastet, Feld 52 nur bis 0,9 m.
     */
    belastet_bis_tiefe: number | null;
    /**
     * Alle beprobten Schichten sind belastet — es gibt keinen unbelasteten
     * Horizont darunter.
     *
     * Verlangt mindestens ZWEI Schichten: bei nur einer beprobten Schicht
     * ist „durchgaengig" keine Aussage ueber die Tiefe, sondern nur ueber
     * diese eine Probe.
     */
    durchgaengig_belastet: boolean;
    /** Mindestens eine Probe steht unter Analytik-Vorbehalt. */
    vorbehalt: boolean;
    /** Keine Probe erfasst — ein Feld ohne Proben ist keine Entwarnung. */
    ohne_proben: boolean;
    /**
     * Proben dieses Feldes, deren Einstufung (noch) nicht uebertragen ist.
     *
     * Das ist der Normalzustand direkt nach dem Erfassen: das Auswahlfeld der
     * EBV-Einstufung steht anfangs leer, weil die Klasse aus dem Gutachten
     * kommt und nicht aus den Messwerten abgeleitet werden darf.
     *
     * Und es ist die gefaehrlichste Luecke des Moduls, weil sie sich wie ein
     * Ergebnis anfuehlt: `ebvRang(null)` ist -1, die Probe geht damit weder in
     * das Maximum ein noch faellt sie auf. Ein Feld mit drei unbewerteten
     * Proben sah bis hierher genauso aus wie ein untersuchtes Feld ohne
     * Befund — und ein Bereich daneben, in dem eine einzige Probe 'BM-0'
     * trug, bekam daraufhin die NIEDRIGSTE Schutzklasse vorgeschlagen.
     *
     * Nicht eingestuft heisst nicht unbelastet. Es heisst nicht beurteilt.
     */
    proben_ohne_einstufung: string[];
    /**
     * Proben ohne verwertbare Tiefenangabe.
     *
     * `durchgaengig_belastet` ist eine Aussage ueber die Schichten MIT Tiefe;
     * eine belastete Probe ohne Tiefenangabe faellt aus dieser Rechnung
     * heraus und wuerde sonst stillschweigend die mittlere statt der
     * hoechsten Klasse nach sich ziehen. Deshalb wird sie ausgewiesen.
     */
    proben_ohne_tiefe: string[];
}

/** Wie eine Probe in einer Luecken-Liste genannt wird. Ohne diesen Ersatz
 *  stuende in der Warnung ein leerer Name, und niemand faende die Zeile. */
const probenName = (p: Probe): string => p.mischprobe.trim() || '(ohne Bezeichnung)';

/**
 * Fasst die Proben EINES Rasterfeldes zusammen.
 *
 * Die Proben muessen bereits zu diesem Feld gehoeren; die Funktion filtert
 * nicht nach `rasterfeld_id` — das ist Aufgabe des Aufrufers und hier
 * bewusst nicht doppelt gemacht, damit nicht zwei Stellen entscheiden,
 * welche Probe zu welchem Feld gehoert.
 */
export const feldEinstufung = (proben: Probe[]): FeldEinstufung => {
    if (proben.length === 0) {
        return {
            klasse: null,
            massgebliche_probe: '',
            belastet_bis_tiefe: null,
            durchgaengig_belastet: false,
            vorbehalt: false,
            ohne_proben: true,
            proben_ohne_einstufung: [],
            proben_ohne_tiefe: [],
        };
    }

    let besterRang = -1;
    let beste: string | null = null;
    let besteProbe = '';
    let belastetBis: number | null = null;
    let belasteteMitTiefe = 0;
    let mitTiefe = 0;
    const ohneEinstufung: string[] = [];
    const ohneTiefe: string[] = [];

    for (const p of proben) {
        const rang = ebvRang(p.einstufung);
        // Rang -1 heisst: leer, unbekannter Schluessel oder eine Altklasse aus
        // einem anderen Regelwerk ('Z2'). In allen drei Faellen ist die Probe
        // NICHT beurteilt — und das muss sichtbar bleiben.
        if (rang < 0) ohneEinstufung.push(probenName(p));
        if (rang > besterRang) {
            besterRang = rang;
            beste = p.einstufung;
            besteProbe = p.mischprobe;
        }

        const klasse = ebvKlasse(p.einstufung);
        const hatTiefe = p.tiefe_bis !== null && Number.isFinite(p.tiefe_bis);
        if (hatTiefe) mitTiefe += 1;
        else ohneTiefe.push(probenName(p));

        if (klasse?.belastet) {
            if (hatTiefe) {
                belasteteMitTiefe += 1;
                belastetBis = belastetBis === null
                    ? (p.tiefe_bis as number)
                    : Math.max(belastetBis, p.tiefe_bis as number);
            }
        }
    }

    return {
        klasse: besterRang >= 0 ? beste : null,
        massgebliche_probe: besterRang >= 0 ? besteProbe : '',
        belastet_bis_tiefe: belastetBis,
        durchgaengig_belastet: mitTiefe >= 2 && belasteteMitTiefe === mitTiefe,
        vorbehalt: proben.some(p => p.vorbehalt),
        ohne_proben: false,
        proben_ohne_einstufung: ohneEinstufung,
        proben_ohne_tiefe: ohneTiefe,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// RASTERFELDER → ARBEITSBEREICH (CLUSTER)
// ═══════════════════════════════════════════════════════════════════════════

export interface ClusterEinstufung {
    /** Maximum ueber alle Felder des Bereichs. */
    klasse: string | null;
    /** Feldnummer, die das Maximum gestellt hat. */
    massgebliches_feld: string;
    /** Groesste nachgewiesene Belastungstiefe im Bereich. */
    belastet_bis_tiefe: number | null;
    /** Mindestens ein Feld ist durchgaengig belastet. */
    durchgaengig_belastet: boolean;
    /** Mindestens eine Probe im Bereich steht unter Vorbehalt. */
    vorbehalt: boolean;
    /** Felder des Bereichs, zu denen keine Probe erfasst ist. */
    felder_ohne_proben: string[];
    /**
     * Felder des Bereichs, in denen mindestens eine Probe ohne Einstufung
     * liegt (`FeldEinstufung.proben_ohne_einstufung`, nach oben gereicht).
     *
     * Steht bewusst neben `felder_ohne_proben` und nicht darin: fuer den
     * Vorschlag sind beide dasselbe — eine Luecke in der Beurteilungs-
     * grundlage —, fuer den Bearbeiter aber nicht. Beim einen fehlt die
     * Probe, beim anderen nur die Uebertragung aus dem Gutachten.
     */
    felder_mit_proben_ohne_einstufung: string[];
}

/** Ein Feld mit seinen Proben, wie es in die Bereichsrechnung geht. */
export interface FeldMitProben {
    feld_nr: string;
    proben: Probe[];
}

/**
 * Fasst die Felder EINES Arbeitsbereichs zusammen — wieder als Maximum.
 *
 * `felder_ohne_proben` ist ausdruecklich Teil des Ergebnisses und nicht nur
 * eine Warnung nebenbei: ein Bereich, dessen Einstufung auf zwei von fuenf
 * beprobten Feldern beruht, ist etwas anderes als ein vollstaendig
 * untersuchter. Die Oberflaeche zeigt das an, und der gedruckte Plan weist
 * es aus.
 */
export const clusterEinstufung = (felder: FeldMitProben[]): ClusterEinstufung => {
    let besterRang = -1;
    let beste: string | null = null;
    let bestesFeld = '';
    let belastetBis: number | null = null;
    let durchgaengig = false;
    let vorbehalt = false;
    const ohneProben: string[] = [];
    const mitUnbewerteterProbe: string[] = [];

    for (const f of felder) {
        const e = feldEinstufung(f.proben);
        if (e.ohne_proben) {
            ohneProben.push(f.feld_nr);
            continue;
        }
        // Vor dem Maximum, nicht danach: ein Feld, dessen Proben ALLE ohne
        // Einstufung sind, liefert Rang -1 und faellt aus jeder Rechnung —
        // gemeldet werden muss es trotzdem.
        if (e.proben_ohne_einstufung.length > 0) mitUnbewerteterProbe.push(f.feld_nr);
        const rang = ebvRang(e.klasse);
        if (rang > besterRang) {
            besterRang = rang;
            beste = e.klasse;
            bestesFeld = f.feld_nr;
        }
        if (e.belastet_bis_tiefe !== null) {
            belastetBis = belastetBis === null
                ? e.belastet_bis_tiefe
                : Math.max(belastetBis, e.belastet_bis_tiefe);
        }
        if (e.durchgaengig_belastet) durchgaengig = true;
        if (e.vorbehalt) vorbehalt = true;
    }

    return {
        klasse: besterRang >= 0 ? beste : null,
        massgebliches_feld: bestesFeld,
        belastet_bis_tiefe: belastetBis,
        durchgaengig_belastet: durchgaengig,
        vorbehalt,
        felder_ohne_proben: ohneProben,
        felder_mit_proben_ohne_einstufung: mitUnbewerteterProbe,
    };
};

/**
 * Die Felder, fuer die dem Bereich eine Beurteilung fehlt — unbeprobte und
 * solche mit unbewerteter Probe, jedes nur einmal.
 *
 * Diese Liste ist der Grund, warum es sie gibt: der Schutzklassenvorschlag
 * darf beide Faelle NICHT unterscheiden. „Keine Probe" und „Probe ohne
 * uebertragene Einstufung" sind fuer die Frage, ob der Bereich beurteilt ist,
 * dasselbe — und in beiden Faellen waere ein Vorschlag auf die niedrigste
 * Klasse eine Unbedenklichkeitsbescheinigung aus fehlenden Daten.
 */
export const beurteilungsluecken = (e: ClusterEinstufung): string[] =>
    [...new Set([...e.felder_ohne_proben, ...e.felder_mit_proben_ohne_einstufung])];

// ═══════════════════════════════════════════════════════════════════════════
// DARSTELLUNG
// ═══════════════════════════════════════════════════════════════════════════

/** Anzeigetext einer Klasse samt Sternchen fuer den Vorbehalt.
 *  'BM-0' + Vorbehalt ergibt 'BM-0*' — so steht es in der Auswertungstabelle
 *  des Gutachters, und so kennt es der Anwender. */
export const klassenLabel = (klasse: string | null, vorbehalt: boolean): string => {
    const k = ebvKlasse(klasse);
    if (!k) return vorbehalt ? '(offen)*' : '(offen)';
    // BM-F0 traegt sein Sternchen bereits im Label.
    if (vorbehalt && !k.label.endsWith('*')) return `${k.label}*`;
    return k.label;
};

/** Farbe fuer Tabelle, Skizze und PDF. Grau fuer „nicht eingestuft" — nicht
 *  gruen: Nichtwissen ist keine Entwarnung. */
export const klassenFarbe = (klasse: string | null): string =>
    ebvKlasse(klasse)?.farbe ?? '#e2e8f0';

/** Legendentexte fuer die unter der Auswertungstabelle abzudruckende
 *  Erklaerung — nur die Klassen, die im Plan tatsaechlich vorkommen. */
export const legendeFuer = (klassen: (string | null)[]): { label: string; text: string }[] => {
    const vorhanden = new Set(klassen.filter(Boolean) as string[]);
    return EBV_KLASSEN
        .filter(k => vorhanden.has(k.key))
        .map(k => ({ label: k.label, text: k.legende }));
};
